import { useEffect, useState } from 'react'
import { api, ApiError } from '../api.ts'

interface Uzytkownik {
  id: string
  code: string
  firstName: string
  lastName: string
  email: string
  role: 'wlasciciel' | 'handlowiec' | 'serwis'
  active: boolean
}
interface InfoFirmy {
  firma: { nazwa: string; adres: string; nip: string; regon: string; konto: string | null }
  poczta: { smtp: boolean; imap: boolean; from: string | null }
}

const ROLE_LABEL: Record<string, string> = { wlasciciel: 'właściciel', handlowiec: 'handlowiec', serwis: 'serwis' }

export function SettingsPage() {
  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-xl font-bold text-slate-800 mb-5">Ustawienia</h1>
      <UsersSection />
      <CompanySection />
    </div>
  )
}

// ── Users ─────────────────────────────────────────────────────────────────────

function UsersSection() {
  const [users, setUsers] = useState<Uzytkownik[]>([])
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  function reload() {
    api.get<{ uzytkownicy: Uzytkownik[] }>('/api/ustawienia/uzytkownicy').then((r) => setUsers(r.uzytkownicy)).catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [])

  const [reset, setReset] = useState<{ code: string; password: string } | null>(null)
  async function resetuj(u: Uzytkownik) {
    if (!confirm(`Nadać ${u.firstName} ${u.lastName} (${u.code}) nowe hasło?\n\nStare przestanie działać od razu; nowe zobaczysz raz i przekażesz tej osobie.`)) return
    setError(null)
    try {
      setReset(await api.post<{ code: string; password: string }>(`/api/ustawienia/uzytkownicy/${u.id}/reset-hasla`))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  async function toggle(u: Uzytkownik) {
    setError(null)
    try {
      await api.patch(`/api/ustawienia/uzytkownicy/${u.id}`, { active: !u.active })
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-700 text-sm">Użytkownicy</h2>
        {!adding && (
          <button onClick={() => setAdding(true)} className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-md hover:bg-brand-700">
            Dodaj użytkownika
          </button>
        )}
      </div>

      {error && <div role="alert" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      {adding && <AddUser onDone={() => { setAdding(false); reload() }} onCancel={() => setAdding(false)} />}

      {reset && (
        <div className="mb-3 text-sm bg-green-50 border border-green-200 rounded-md px-3 py-2">
          Nowe hasło dla <strong>{reset.code}</strong> (pokazane tylko teraz — przekaż je bezpiecznie):{' '}
          <span className="font-mono font-bold text-brand-700 text-base">{reset.password}</span>
          <button onClick={() => setReset(null)} className="ml-3 text-xs text-slate-500 underline">
            ukryj
          </button>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-400">
          <tr>
            <th className="text-left font-medium py-1">Kod</th>
            <th className="text-left font-medium py-1">Osoba</th>
            <th className="text-left font-medium py-1">Rola</th>
            <th className="text-right font-medium py-1">Stan</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-slate-100">
              <td className="py-1.5 font-mono font-medium text-slate-800">{u.code}</td>
              <td className="py-1.5 text-slate-700">
                {u.firstName} {u.lastName}
                <span className="block text-xs text-slate-400">{u.email}</span>
              </td>
              <td className="py-1.5 text-slate-600">{ROLE_LABEL[u.role]}</td>
              <td className="py-1.5 text-right whitespace-nowrap">
                <button onClick={() => void resetuj(u)} className="text-xs text-slate-400 hover:text-brand-700 mr-3" title="Nadaj nowe hasło jednorazowe">
                  resetuj hasło
                </button>
                {u.active ? (
                  <button onClick={() => void toggle(u)} className="text-xs text-slate-500 hover:text-red-700" title="Kliknij, żeby wyłączyć">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1 align-middle" />aktywny
                  </button>
                ) : (
                  <button onClick={() => void toggle(u)} className="text-xs text-slate-400 hover:text-green-700" title="Kliknij, żeby włączyć">
                    <span className="inline-block w-2 h-2 rounded-full bg-slate-300 mr-1 align-middle" />wyłączony
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-2">Wyłączony użytkownik nie może się zalogować, ale jego historia zostaje. Nie usuwamy kont.</p>
    </section>
  )
}

function AddUser({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'wlasciciel' | 'handlowiec' | 'serwis'>('handlowiec')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [haslo, setHaslo] = useState<{ code: string; password: string } | null>(null)

  async function submit() {
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<{ code: string; password: string }>('/api/ustawienia/uzytkownicy', { code, firstName, lastName, email, role })
      setHaslo(r)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  if (haslo) {
    return (
      <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-sm font-medium text-green-800 mb-1">Dodano użytkownika {haslo.code}</div>
        <div className="text-sm text-slate-700">
          Hasło (pokazane tylko teraz — przekaż je bezpiecznie):{' '}
          <span className="font-mono font-bold text-brand-700 text-base">{haslo.password}</span>
        </div>
        <button onClick={onDone} className="mt-3 text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700">Gotowe</button>
      </div>
    )
  }

  return (
    <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Kod (np. AB)" className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" maxLength={3} />
        <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
          <option value="handlowiec">handlowiec</option>
          <option value="serwis">serwis</option>
          <option value="wlasciciel">właściciel</option>
        </select>
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Imię" className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Nazwisko" className="px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
      </div>
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-sm text-slate-500 px-3 py-1.5">Anuluj</button>
        <button onClick={() => void submit()} disabled={busy} className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50">Dodaj</button>
      </div>
    </div>
  )
}

// ── Company data + mailbox (read-only) ────────────────────────────────────────

function CompanySection() {
  const [info, setInfo] = useState<InfoFirmy | null>(null)
  useEffect(() => {
    api.get<InfoFirmy>('/api/ustawienia/firma').then(setInfo).catch(() => {})
  }, [])
  if (!info) return null

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4">
      <h2 className="font-semibold text-slate-700 text-sm mb-3">Dane firmy i poczta</h2>
      <dl className="text-sm grid grid-cols-[8rem_1fr] gap-x-4 gap-y-1 mb-3">
        <dt className="text-slate-500">Nazwa</dt><dd className="text-slate-800">{info.firma.nazwa}</dd>
        <dt className="text-slate-500">Adres</dt><dd className="text-slate-800">{info.firma.adres}</dd>
        <dt className="text-slate-500">NIP</dt><dd className="text-slate-800">{info.firma.nip}</dd>
        <dt className="text-slate-500">REGON</dt><dd className="text-slate-800">{info.firma.regon}</dd>
        <dt className="text-slate-500">Konto</dt><dd className={info.firma.konto ? 'text-slate-800' : 'text-amber-700'}>{info.firma.konto ?? 'nie ustawiono — nie pojawi się na ofercie'}</dd>
      </dl>
      <div className="text-sm flex flex-wrap gap-4 mb-2">
        <Status ok={info.poczta.smtp} label={`Wysyłka poczty (SMTP)${info.poczta.from ? ` · ${info.poczta.from}` : ''}`} />
        <Status ok={info.poczta.imap} label="Kopia w Wysłanych (IMAP)" />
      </div>
      <p className="text-[11px] text-slate-400">Dane firmy i skrzynkę ustawia się w pliku konfiguracyjnym na serwerze (.env). Napisz, jeśli chcesz to zmienić z poziomu aplikacji — dołożę edycję.</p>
    </section>
  )
}

function Status({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={ok ? 'text-green-700' : 'text-slate-400'}>
      <span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${ok ? 'bg-green-500' : 'bg-slate-300'}`} />
      {label} {ok ? '' : '— nieskonfigurowane'}
    </span>
  )
}
